import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import {
  ArcElement,
  BarController,
  BarElement,
  BubbleController,
  CategoryScale,
  Chart,
  Decimation,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  LogarithmicScale,
  PieController,
  PointElement,
  PolarAreaController,
  RadarController,
  RadialLinearScale,
  ScatterController,
  SubTitle,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {ExchangePriceControllerService, ExchangePriceWithRelations} from "@aparraga/ahoracrypto-api-sdk/sdk";
import {ExchangeService} from "@aparraga/ahoracrypto-client-common/src/app/common/exchange/exchange.service";
import {BehaviorSubject} from "rxjs";
import {DeviceDetectorService} from "ngx-device-detector";
import {I18nServices} from "@aparraga/ahoracrypto-client-common/src/app/common/i18n/i18n.service";
import {DOCUMENT} from "@angular/common";
import {IS_BROWSER} from "@aparraga/ahoracrypto-client-common/src/app/common/utils/injectiontokens";

@Component({
    selector: 'chart',
    templateUrl: './chart.component.html',
    styleUrls: ['./chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ChartComponent implements AfterViewInit, OnDestroy {

  protected subscriptions: Array<any> = [];

  @ViewChild('stockPriceChart') stockPriceChartContainer: ElementRef;
  @ViewChild('chartNavigator') chartNavigatorContainer: ElementRef;

  protected stockPriceChart: Chart;
  protected chartNavigator: Chart;

  _baseCurrency: string;
  get baseCurrency(): string {
    return this._baseCurrency;
  }
  @Input() set baseCurrency(value: string) {
    this._baseCurrency = value;
    this.refreshChartData();
  }

  _quoteCurrency: string;
  get quoteCurrency(): string {
    return this._quoteCurrency;
  }
  @Input() set quoteCurrency(value: string) {
    this._quoteCurrency = value;
    this.refreshChartData();
  }

  _exchangePrice: ExchangePriceWithRelations;
  get exchangePrice(): ExchangePriceWithRelations {
    return this._exchangePrice;
  }
  @Input() set exchangePrice(value: ExchangePriceWithRelations) {
    this._exchangePrice = value;
    this._baseCurrency = value.baseCurrency;
    this._quoteCurrency = value.quoteCurrency;
    this.refreshChartData();
  }

  _chartTimeFrame: string = '1d';
  get chartTimeFrame(): string {
    return this._chartTimeFrame;
  }
  @Input() set chartTimeFrame(value: string) {
    this._chartTimeFrame = value;
    this.refreshChartData();
  }

  protected dataRefreshed$ = new BehaviorSubject(false);

  protected interval: any = 3;
  protected dates = [];
  protected numbers = [];
  protected volume = [];

  public greenColor = 'rgba(22, 196, 132, 1)';
  public redColor = 'rgba(235, 57, 67, 1)';
  public grayLinesColor = 'rgba(239, 242, 245, 1)';
  public coordinatesLineColor = 'rgba(166, 176, 195, 1)';

  public isMobile = false;

  public chartAspectRatio = 2.5;
  public chartNavigatorAspectRatio = 15;

  public constructor(protected exchangePriceControllerService: ExchangePriceControllerService,
                     protected i18nService: I18nServices,
                     @Inject(DOCUMENT) protected document: Document,
                     @Inject(IS_BROWSER) protected isBrowser: boolean,
                     protected deviceService: DeviceDetectorService,
                     protected exchangeService: ExchangeService) {

    this.isMobile = this.deviceService.isMobile();
    if(this.deviceService.isMobile()) {
      this.chartAspectRatio = 1.5;
      this.chartNavigatorAspectRatio = 7;
    }

  }

  //todo ...
  public updateCryptoInformation() {

  }

  public ngAfterViewInit() {

    Chart.defaults.font.family = "'FontAwesome', 'Helvetica', 'Helvetica Neue', 'Arial', sans-serif";
    Chart.register(
      ArcElement,
      LineElement,
      BarElement,
      PointElement,
      BarController,
      BubbleController,
      DoughnutController,
      LineController,
      PieController,
      PolarAreaController,
      RadarController,
      ScatterController,
      CategoryScale,
      LinearScale,
      LogarithmicScale,
      RadialLinearScale,
      TimeScale,
      TimeSeriesScale,
      Decimation,
      Filler,
      Legend,
      Title,
      Tooltip,
      SubTitle
    );

    this.subscriptions.push(this.dataRefreshed$.subscribe(refreshed => {
      if(refreshed) {
        this.zoomBox(this.dates[0], this.dates[this.dates.length - 1]);
      }
    }));

    this.document.defaultView.addEventListener('resize', (e) => {
      this.chartNavigator.resize();
      this.zoomBox(this.stockPriceChart.config.options.scales['x'].min, this.stockPriceChart.config.options.scales['x'].max);
    })

  }

  public ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    } );
  }

  protected refreshChartData() {

    let dateStart = new Date();
    let dateEnd = new Date();
    this.interval = 3;

    switch(this._chartTimeFrame) {
      case '7d':
        dateStart.setDate(dateStart.getDate() - 7);
        this.interval = 6; //hourly
        break;
      default:
      case '1d':
        dateStart.setDate(dateStart.getDate() - 1);
        this.interval = 3; //5 minutes
        break;
    }

    dateStart.setMinutes(0, 0);

    if(this._baseCurrency != null && this._quoteCurrency != null) {

      let stableQuoteCurrency = this.exchangeService.getStableCoin(this._quoteCurrency);

      this.subscriptions.push(this.exchangePriceControllerService.exchangePriceControllerGetCandlestickBars(this._baseCurrency, stableQuoteCurrency, dateStart.getTime(), dateEnd.getTime(), this.interval).subscribe({
        next: (candlestickBars) => {
          if (!candlestickBars || candlestickBars.length === 0) {
            console.warn('No candlestick data received for', this._baseCurrency);
            return;
          }

        /**
         * Example of candlestick bar:
         *
         * {
         * "id":"88927d5f1fc32f5ad5f3de7b55e2d290",
         * "version":null,
         * "baseCurrency":"BTC",
         * "quoteCurrency":"EUR",
         * "interval":6,
         * "openTime":1660417200000,
         * "closeTime":1660420799999,
         * "open":24153.375,
         * "high":24478.22,
         * "low":23832.41,
         * "close":24159.355,
         * "volume":902.50027,
         * "quoteAssetVolume":2.20641283634368E7,
         * "numberOfTrades":37880,
         * "takerBuyBaseAssetVolume":450.62502,
         * "takerBuyQuoteAssetVolume":1.10184470478065E7
         * }
         */

        let volume = [];
        this.dates = [];
        this.numbers = [];
        let maxVolume = null;

        let showIfDesktop = true;
        candlestickBars.forEach(candlestickBar => {
          if(!this.isMobile || showIfDesktop) {
            this.dates.push(this.transformToNearestXAxisValue(new Date(candlestickBar.openTime)));
            this.numbers.push(candlestickBar.open);
            volume.push(candlestickBar.volume);
            if (maxVolume == null || maxVolume < candlestickBar.volume) {
              maxVolume = candlestickBar.volume;
            }
          }
          showIfDesktop = !showIfDesktop;
        });

        // Fix division by zero when calculating volume percentages
        this.volume = [];
        volume.forEach(volumeItem => {
          if (maxVolume && maxVolume > 0.00001) {
            this.volume.push(volumeItem * 100 / maxVolume);
          } else {
            this.volume.push(0);
          }
        });

        this.configureCharts();
        },
        error: (error) => {
          console.error('Error fetching candlestick data:', error);
        }
      }));
    }

  }

  protected transformToNearestXAxisValue(date: Date) {

    let returnValue = null;

    switch(this.interval) {
      case 6: //every hour
        returnValue = date.setMinutes(0, 0, 0);
        break;
      default:
      case 3: //every 5 minutes
        returnValue = date.setMinutes(Math.floor(date.getMinutes() / 5) * 5, 0, 0);
        break;
    }

    return returnValue;

  }

  protected configureCharts() {

    // setup
    const data = {
      labels: this.dates,
      datasets: [{
        label: 'Crypto prices',
        data: this.numbers,
        fill: {
          target: {
            value: this.numbers[0]
          },
          below: (context) => {
            const chart = context.chart;
            const { ctx, chartArea, data, scales } = chart;
            if(!chartArea) {
              return null;
            }
            return this.belowGradient(ctx, chartArea, data, scales);
          },
          above: (context) => {
            const chart = context.chart;
            const { ctx, chartArea, data, scales } = chart;
            if(!chartArea) {
              return null;
            }
            return this.aboveGradient(ctx, chartArea, data, scales);
          },
        },
        borderColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea, data, scales } = chart;
          if(!chartArea) {
            return null;
          }
          return this.getGradient(ctx, chartArea, data, scales)
        },
        tension: 0,
        pointRadius: 0,
        pointHitRadius: 0,
        pointHoverRadius: 0,
        borderWidth: this.isMobile?1:2,
      },{
        label: 'Stock Volume',
        type: 'bar',
        data: this.volume,
        pointHitRadius: 0,
        pointHoverRadius: 0,
        yAxisID: 'volume'
      }
      ]
    };

    // dottedLine plugin block
    const dottedLine = {
      id: 'dottedLine',
      beforeDatasetsDraw: (chart, args, pluginOptions) => {
        const { ctx, data, chartArea: {left, right, width}, scales: {x, y} } = chart;

        const startingPoint = data.datasets[0].data[data.labels.indexOf(x.min)];

        ctx.save();
        ctx.beginPath();
        ctx.lineWidth = 1;
        ctx.setLineDash([1, 5]);
        ctx.strokeStyle = 'rgba(102, 102, 102, 1)';
        ctx.moveTo(left, y.getPixelForValue(startingPoint));
        ctx.lineTo(right, y.getPixelForValue(startingPoint));
        ctx.stroke();
        ctx.closePath();
        ctx.setLineDash([])

        ctx.beginPath();
        ctx.fillStyle = 'rgba(102, 102, 102, 1)'; // fixed gray box with start value at y axis
        ctx.fillRect(0, y.getPixelForValue(startingPoint) - 10, left, 20)
        ctx.closePath();

        ctx.font = '12px sans-serif';
        ctx.fillStyle = 'white';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(this.i18nService.formatCurrency(startingPoint, this._quoteCurrency), left / 2, y.getPixelForValue(startingPoint));
      }
    };

    let imageLogo = {};
    if(!this.isMobile && this.isBrowser) {
      // imageLogo plugin block
      const logo = new Image();
      logo.src = 'assets/images/logo-watermark.png';
      imageLogo = {
        id: 'imageLogo',
        beforeDatasetsDraw: (chart, args, pluginOptions) => {
          const {ctx, chartArea: {top, bottom, left, right}, scales: {x, y}} = chart;
          ctx.save();
          const imgWidth = 200;
          const imgHeight = 35;

          if (logo.complete) {
            ctx.drawImage(logo, right - imgWidth - 20, bottom - imgHeight - 70, imgWidth, imgHeight)
          } else {
            logo.onload = () => chart.draw();
          }
          ctx.restore();
        }
      }
    }

    // customTooltip plugin block
    const customTooltip = {
      id: 'customTooltip',
      afterDraw: (chart, args, pluginOptions) => {
        const { ctx, chartArea: {top, bottom, left, right, width, height }, scales: {x, y} } = chart;
        ctx.save();
        chart.canvas.addEventListener('mousemove', (e) => {
          this.tooltipPosition(e, chart);
        });
      }
    };

    // config
    const config: any = {
      type: 'line',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        layout: {
          padding: {
            left: 10,
            right: 5
          }
        },
        aspectRatio: this.chartAspectRatio,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'minute'
            },
            min: this.dates[0],
            max: this.dates[this.dates.length - 1],
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
              drawBorder: false,
              offset: false
            },
            ticks: {
              //this is the callback to render the hours at X axis
              callback: (value, index, values) => {
                let currentTick = new Date(values[index].value);
                if(this._chartTimeFrame == '1d') {
                  if(currentTick.getMinutes() == 0) {
                    if (currentTick.getHours() == 0) {
                      return currentTick.getDate();
                    } else {
                      return currentTick.getHours() % 3 == 0 ? currentTick.getHours() + ':00' : '';
                    }
                  }
                }
                else if(this._chartTimeFrame == '7d') {
                  return null;
                }
                return null;
              },
              font: {
                weight: (values) => {
                  let currentTick = new Date(values.tick.value);
                  if(this._chartTimeFrame == '1d') {
                    if(currentTick.getHours() == 0 && currentTick.getMinutes() == 0) {
                      return 'bold';
                    }
                  }
                  return null;
                }
              }
            }
          },
          y: {
            beginAtZero: false,
            grid: {
              color: this.grayLinesColor
            },
            ticks: {
              //this is the callback to render the hours at X axis
              callback: (value, index, values) => {
                return this.i18nService.formatCurrency( value, this._quoteCurrency)
              }
            }
          },
          volume: {
            type: 'linear',
            position: 'right',
            min: 0,
            max: 1000,
            grid: {
              display: false
            },
            ticks: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        }
      },
      plugins: [dottedLine, imageLogo, customTooltip]
    };

    // Destroy and render init block
    this.stockPriceChart?.destroy();
    this.stockPriceChart = new Chart(
      this.stockPriceChartContainer.nativeElement,
      config
    );

    this.stockPriceChart.canvas.addEventListener('mousemove', (e) => {
      this.crosshairLine(this.stockPriceChart, e)
    });

    this.stockPriceChart.canvas.addEventListener('wheel', (e) => {
      this.zoom(this.stockPriceChart, e)
    });

    // setup
    const dataChartNavigator = {
      labels: this.dates,
      datasets: [{
        data: this.numbers,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        borderWidth: 1
      }]
    };

    // config
    const configChartNavigator: any = {
      type: 'line',
      data: dataChartNavigator,
      options: {
        responsive: true,
        animation: false,
        layout: {
          padding: {
            left: this.stockPriceChart.chartArea.left - this.stockPriceChart.config.options.layout.padding['left'],
            right: this.stockPriceChart.width - this.stockPriceChart.chartArea.right,
          }
        },
        aspectRatio: this.chartNavigatorAspectRatio,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day'
            },
            min: this.dates[0],
            max: this.dates[this.dates.length - 1],
            grid: {
              drawBorder: false,
              drawTicks: false,
            },
            ticks: {
              mirror: true,
            }
          },
          y: {
            beginAtZero: false,
            ticks: {
              display: false,
            },
            grid: {
              display: false,
              drawBorder: false,
            }
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false
          }
        }
      }
    };

    // render init block
    this.chartNavigator?.destroy();
    this.chartNavigator = new Chart(
      this.chartNavigatorContainer.nativeElement,
      configChartNavigator
    );

    this.dataRefreshed$.next(true)

  }

  protected tooltipPosition(mousemove, chart){

    const { ctx, chartArea: {top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    let xTooltip;
    let yTooltip;
    const rightSide = right - mousemove.offsetX;
    if(rightSide <= 170) {
      xTooltip = mousemove.offsetX - 170;
    } else {
      xTooltip = mousemove.offsetX + 20;
    }

    if(mousemove.offsetY <= 100) {
      yTooltip = mousemove.offsetY + 30;
    } else {
      yTooltip = mousemove.offsetY - 80;
    }

    let xleft;
    let xright;
    if(x.min = this.dates[0]) {
      xleft = x.getPixelForValue(this.dates[0]);
    } else {
      xleft = left;
    }

    if(x.max = this.dates[this.dates.length -1]) {
      xright = x.getPixelForValue(this.dates[this.dates.length -1]);
    } else {
      xright = right;
    }

    if(mousemove.offsetX >= xleft && mousemove.offsetX <= xright && mousemove.offsetY >= top && mousemove.offsetY <= bottom) {

      ctx.beginPath();
      ctx.fillStyle = 'rgba(255, 255, 255, 1)';
      ctx.strokeStyle = 'rgba(102, 102, 102, 1)';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 1;
      ctx.fillRect(xTooltip, yTooltip, 150, 60);
      ctx.strokeRect(xTooltip, yTooltip, 150, 60);
      ctx.closePath();

      const dateCursor = new Date(x.getValueForPixel(mousemove.offsetX));
      const dateIndex = this.dates.indexOf(this.transformToNearestXAxisValue(dateCursor))
      if(dateIndex >= 0) {

        // text date
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = 'bolder 12px sans-serif';
        ctx.fillText(dateCursor.toLocaleDateString(), xTooltip + 5, yTooltip + 10);

        // text time
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = 'bolder 10px sans-serif';
        ctx.fillText(new Date(x.getValueForPixel(mousemove.offsetX)).toLocaleTimeString(), xTooltip + 150 - 5, yTooltip + 10);

        // Line 2 Color DOT
        let dotColor;
        if (this.numbers[dateIndex] >= this.numbers[0]) {
          dotColor = this.greenColor;
        } else {
          dotColor = this.redColor;
        }

        const dotSpace = 15;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = dotColor;
        ctx.font = 'bolder 12px FontAwesome';
        ctx.fillText('\uf111', xTooltip + 5, yTooltip + 30);

        // Line 2 Text Price
        const priceText = 'Price: ';
        const priceTextWidth = ctx.measureText(priceText).width;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = '12px sans-serif';
        ctx.fillText(priceText, xTooltip + 5 + dotSpace, yTooltip + 30);

        // Line 2 Price value
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'black';
        ctx.font = 'bolder 12px sans-serif';
        ctx.fillText(this.i18nService.formatCurrency( this.numbers[dateIndex], this._quoteCurrency), xTooltip + 5 + dotSpace + priceTextWidth, yTooltip + 30);

        // Line 3 Icon
        const iconSpace = 15;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = 'bolder 12px FontAwesome';
        ctx.fillText('\uf080', xTooltip + 5, yTooltip + 50);

        // Line 3 Text Value
        const valueText = 'Value: ';
        const valueTextWidth = ctx.measureText(valueText).width;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = '12px sans-serif';
        ctx.fillText(valueText, xTooltip + 5 + iconSpace, yTooltip + 50);

        // Line 3 Value value
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = 'bolder 12px sans-serif';
        //the volume amount within the tooltip
        ctx.fillText(this.volume[dateIndex].toFixed(0), xTooltip + 5 + iconSpace + valueTextWidth, yTooltip + 50);

      }
      ctx.restore();

    }
  }


  protected getGradient(ctx, chartArea, data, scales) {
    const {left, right, top, bottom, width, height} = chartArea;
    const {x, y} = scales;
    const gradientBorder = ctx.createLinearGradient(0,0,0,bottom);
    
    // Fix issue with shift calculation
    let shift = 0.5;
    const dataIndex = data.labels.indexOf(x.min);
    if (dataIndex >= 0 && 
        dataIndex < data.datasets[0].data.length && 
        data.datasets[0].data[dataIndex] !== undefined && 
        bottom > 0) {
      const value = data.datasets[0].data[dataIndex];
      const pixelValue = y.getPixelForValue(value);
      
      if (typeof pixelValue === 'number' && isFinite(pixelValue)) {
        shift = pixelValue / bottom;
      }
    }
    
    if (!isFinite(shift)) {
      shift = 0.5;
    }

    if(shift > 1) {
      shift = 1;
    }

    if(shift < 0) {
      shift = 0;
    }

    gradientBorder.addColorStop(0, this.greenColor);
    gradientBorder.addColorStop(shift, this.greenColor);
    gradientBorder.addColorStop(shift, this.redColor);
    gradientBorder.addColorStop(1, this.redColor);
    return gradientBorder;
  }

  protected belowGradient(ctx, chartArea, data, scales) {
    const {left, right, top, bottom, width, height} = chartArea;
    const {x, y} = scales;
    const gradientBackground = ctx.createLinearGradient(0, y.getPixelForValue(data.datasets[0].data[0]), 0, bottom);
    //red gradient
    gradientBackground.addColorStop(0, 'rgba(255, 26, 104, 0)');
    gradientBackground.addColorStop(1, 'rgba(255, 26, 104, 0.5)');
    return gradientBackground;
  }

  protected aboveGradient(ctx, chartArea, data, scales) {
    const {left, right, top, bottom, width, height} = chartArea;
    const {x, y} = scales;
    const gradientBackground = ctx.createLinearGradient(0, y.getPixelForValue(data.datasets[0].data[0]), 0, top);
    //green gradient
    gradientBackground.addColorStop(0, 'rgba(75, 192, 192, 0)');
    gradientBackground.addColorStop(1, 'rgba(75, 192, 192, 0.5)');
    return gradientBackground;
  }

  protected crosshairLine(chart, mousemove){
    const { canvas, ctx, chartArea: { left, right, top, bottom } } = chart;

    const coorX = mousemove.offsetX;
    const coorY = mousemove.offsetY;

    chart.update('none');
    ctx.restore();

    if(coorX >= left && coorX <= right && coorY >= top && coorY <= bottom) {
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = 'default';
    }

    ctx.strokeStyle = '#666';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);

    if(coorX >= left && coorX <= right && coorY >= top && coorY <= bottom) {
      // Horizontal Line
      ctx.beginPath();
      ctx.moveTo(left, coorY);
      ctx.lineTo(right, coorY);
      ctx.stroke();
      ctx.closePath();

      // Vertical Line
      ctx.beginPath();
      ctx.moveTo(coorX, top);
      ctx.lineTo(coorX, bottom);
      ctx.stroke();
      ctx.closePath();
      this.crosshairLabel(chart, mousemove);
      this.crosshairPoint(chart, mousemove);
    }
    ctx.setLineDash([]);
  }

  protected crosshairLabel(chart, mousemove){
    const { ctx, data, chartArea: {top, bottom, left, right, width, height}, scales: {x, y} } = chart;

    const coorX = mousemove.offsetX;
    const coorY = mousemove.offsetY;
    const textWidth = ctx.measureText(new Date(x.getValueForPixel(coorX)).toLocaleString()).width + 10;

    ctx.font = '12px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    // yLabel
    ctx.beginPath();
    ctx.fillStyle = 'rgba(132, 132, 132, 1)'; // gray box with exact value at y axis
    ctx.fillRect(0, coorY - 10, left, 20);
    ctx.closePath();

    ctx.fillStyle = 'white';
    ctx.fillText(this.i18nService.formatCurrency(y.getValueForPixel(coorY), this._quoteCurrency), left / 2, coorY);

    // xLabel
    ctx.beginPath();
    ctx.fillStyle = 'rgba(132, 132, 132, 1)'; // gray box with exact value at x axis
    ctx.fillRect(coorX - (textWidth / 2), bottom, textWidth, 20);
    ctx.closePath();

    ctx.fillStyle = 'white';
    ctx.fillText(new Date(x.getValueForPixel(coorX)).toLocaleString(), coorX, bottom + 10);
  }

  //this is the small ball close to the chart line when moving the cursor
  protected crosshairPoint(chart, mousemove) {
    const { ctx, data, chartArea: {top, bottom, left, right, width, height}, scales: {x, y} } = chart;

    const coorX = mousemove.offsetX;

    ctx.beginPath();
    ctx.strokeStyle = '#FFF';
    ctx.lineWidth = 3;
    ctx.setLineDash([]);

    const angle = Math.PI / 180;

    const leftOffset = x.getPixelForValue(x.min) - left;
    const rightOffset = right - x.getPixelForValue(x.max);

    const width2 = width - (leftOffset + rightOffset);

    const segments = width2 / (this.dates.indexOf(x.max) - this.dates.indexOf(x.min));

    const yOpening = y.getPixelForValue(data.datasets[0].data[0]); // solid
    let index = Math.floor((coorX - (left + leftOffset)) / segments) + this.dates.indexOf(x.min) ;

    let yStart = y.getPixelForValue(data.datasets[0].data[index]);
    let yEnd = y.getPixelForValue(data.datasets[0].data[index + 1]);

    let yInterpolation = yStart + ((yEnd - yStart) / segments * (coorX - x.getPixelForValue(data.labels[index])) );

    if(yOpening >= yInterpolation) {
      ctx.fillStyle = this.greenColor;
    } else {
      ctx.fillStyle = this.redColor;
    }

    // draw the circle
    ctx.arc(
      coorX,
      yInterpolation,
      5,
      angle * 0,
      angle * 360,
      false
    );
    ctx.fill();
    ctx.stroke();
  }

  protected zoom(chart, mousewheel){
    const min = chart.config.options.scales.x.min;
    const max = chart.config.options.scales.x.max;

    const timestamp = chart.scales.x.getValueForPixel(mousewheel.offsetX)
    const dayTimestamp = this.transformToNearestXAxisValue(new Date(timestamp));
    const scrollPoint = this.dates.indexOf(dayTimestamp);

    if(mousewheel.wheelDeltaY >= 0) {
      chart.config.options.scales.x.min = this.dates[this.dates.indexOf(min) + 1];
      chart.config.options.scales.x.max = this.dates[this.dates.indexOf(max) - 1];

      if(this.dates[this.dates.indexOf(min)] <= 0 ) {
        chart.config.options.scales.x.min = this.dates[0];
      }

      if(this.dates.indexOf(min) >= scrollPoint - 4 && this.dates.indexOf(min) <= scrollPoint) {
        chart.config.options.scales.x.min = min;
      }

      if(this.dates.indexOf(max) <= scrollPoint + 4 && this.dates.indexOf(max) >= scrollPoint) {
        chart.config.options.scales.x.max = max;
      }
    }

    if(mousewheel.wheelDeltaY < 0) {
      chart.config.options.scales.x.min = this.dates[this.dates.indexOf(min) - 1];
      chart.config.options.scales.x.max = this.dates[this.dates.indexOf(max) + 1];

      if(this.dates[this.dates.indexOf(max)] >= this.dates[this.dates.length - 1] ) {
        chart.config.options.scales.x.max = this.dates[this.dates.length - 1];
      }

      const weekms = 86400000 * 14;
      const range = max - min;
      if(range >= weekms){
        if(this.dates.indexOf(min) >= scrollPoint - 4 && this.dates.indexOf(min) <= scrollPoint) {
          chart.config.options.scales.x.min = min;
        }

        if(this.dates.indexOf(max) <= scrollPoint + 4 && this.dates.indexOf(max) >= scrollPoint) {
          chart.config.options.scales.x.max = max;
        }
      }
    }

    this.zoomBox(min, max);
    chart.update('none');

  }

  protected zoomBox(min, max) {

    this.chartNavigator.update('none');
    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = this.chartNavigator;

    if(min === undefined) {
      min = this.dates[0];
    }

    this.zoomBoxItem(min, max, this.chartNavigator);

    canvas.addEventListener('mousemove', (e) => {
      this.mouseCursor(e, this.chartNavigator)
    });


    canvas.addEventListener('mousedown', (e) => {
      this.dragStart(e, this.chartNavigator);
    });

    this.document.defaultView.addEventListener('mouseup', (e) => {
      canvas.onmousemove = null;
    })

  }

  protected zoomBoxItem(min, max, chart) {

    if(min === undefined || min === -1) {
      min = this.dates[0];
    }

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = 'rgba(54, 162, 235, 0.2)';
    ctx.fillRect(x.getPixelForValue(min), top, x.getPixelForValue(max) - x.getPixelForValue(min), height);
    ctx.closePath();
    ctx.restore();

    const angle = Math.PI / 180;

    this.swiperButton(x.getPixelForValue(min), angle, chart);
    this.swiperButton(x.getPixelForValue(max), angle, chart);

  }

  protected swiperButton(position, angle, chart) {

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    ctx.beginPath();
    ctx.strokeStyle = 'rgba(54, 162, 235, 1)';
    ctx.lineWidth = 2;
    ctx.fillStyle = '#FFF';
    ctx.arc(position, height / 2, 10, angle * 0, angle * 360, false);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
    ctx.restore();

    ctx.strokeStyle = 'rgba(54, 162, 235, 1)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(position - 3, height / 2 - 5);
    ctx.lineTo(position - 3, height / 2 + 5);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(position + 3, height / 2 - 5);
    ctx.lineTo(position + 3, height / 2 + 5);
    ctx.stroke();
    ctx.restore();
  }

  protected mouseCursor(mousemove, chart) {

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    let minChart1: any = this.stockPriceChart.config.options.scales['x'].min;

    if(minChart1 === undefined || minChart1 === -1) {
      minChart1 = this.dates[0];
    }

    if(mousemove.offsetX >= x.getPixelForValue(minChart1) - 10 && mousemove.offsetX <= x.getPixelForValue(minChart1) + 10
      ||
      mousemove.offsetX >= x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) - 10 && mousemove.offsetX <= x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) + 10
    ) {
      canvas.style.cursor = 'ew-resize';
    } else if (mousemove.offsetX > x.getPixelForValue(minChart1) + 10 && mousemove.offsetX < x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) - 10) {
      canvas.style.cursor = 'move';
    } else {
      canvas.style.cursor = 'default';
    }
  }

  protected dragStart(drag, chart) {

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    let minXValue: any = this.stockPriceChart.config.options.scales['x'].min;
    let maxXValue = this.stockPriceChart.config.options.scales['x'].max;

    if(minXValue === undefined || minXValue === -1) {
      minXValue = this.dates[0];
    }

    if(drag.offsetX >= x.getPixelForValue(minXValue) - 10 && drag.offsetX <= x.getPixelForValue(minXValue) + 10) {
      canvas.onmousemove = (e) => {
        this.dragMove(this.stockPriceChart, e, chart, 'left');
      }
    }

    if(drag.offsetX >= x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) - 10 && drag.offsetX <= x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) + 10) {
      canvas.onmousemove = (e) => {
        this.dragMove(this.stockPriceChart, e, chart, 'right');
      }

    }

    if(drag.offsetX > x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].min as number) + 11 && drag.offsetX < x.getPixelForValue(this.stockPriceChart.config.options.scales['x'].max as number) - 11) {
      canvas.onmousemove = (e) => {
        this.dragMoveCenter(this.stockPriceChart, e, minXValue, maxXValue, drag, chart);
      }
    }
  }

  protected dragMove(stockPriceChart, dragDelta, chart, position) {

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    const timestamp = x.getValueForPixel(dragDelta.offsetX);
    const dayTimestamp = this.transformToNearestXAxisValue(new Date(timestamp));
    let scrollPoint = this.dates.indexOf(dayTimestamp);

    if(dragDelta.offsetX < left && scrollPoint === -1) {
      scrollPoint = 0;
    }

    if(position == 'left') {
      if(dragDelta.offsetX > right && scrollPoint === -1) {
        scrollPoint = this.dates.indexOf(stockPriceChart.config.options.scales.x.max) - 4;
      }

      if(scrollPoint > this.dates.indexOf(stockPriceChart.config.options.scales.x.max) - 4) {
        scrollPoint = this.dates.indexOf(stockPriceChart.config.options.scales.x.max) - 4;
      }

      stockPriceChart.config.options.scales['x'].min = this.dates[scrollPoint];
      stockPriceChart.update('none');
      this.chartNavigator.update('none');

      this.zoomBoxItem(this.dates[scrollPoint], stockPriceChart.config.options.scales.x.max, this.chartNavigator);
    }
    else if(position == 'right') {
      if(dragDelta.offsetX > right && scrollPoint === -1) {
        scrollPoint = this.dates.length - 1;
      }

      if(dragDelta.offsetX < left && scrollPoint === -1) {
        scrollPoint = this.dates.indexOf(stockPriceChart.config.options.scales.x.min) + 4;
      }

      if(scrollPoint < this.dates.indexOf(stockPriceChart.config.options.scales.x.min) + 4) {
        scrollPoint = this.dates.indexOf(stockPriceChart.config.options.scales.x.min) + 4;
      }

      stockPriceChart.config.options.scales.x.max = this.dates[scrollPoint];
      stockPriceChart.update('none');
      this.chartNavigator.update('none');
      this.zoomBoxItem(this.dates[scrollPoint], stockPriceChart.config.options.scales.x.min, this.chartNavigator);
    }

  }

  protected dragMoveCenter(stockPriceChart, dragDelta, staticScaleMin, staticScaleMax, drag, chart) {

    const { ctx, canvas, chartArea: { top, bottom, left, right, width, height }, scales: {x, y} } = chart;

    // starting point
    const dragStartingPoint = x.getValueForPixel(drag.offsetX);
    const dayDragStartingPoint = this.transformToNearestXAxisValue(new Date(dragStartingPoint));
    let dragStart = this.dates.indexOf(dayDragStartingPoint);

    // difference
    const timestamp = x.getValueForPixel(dragDelta.offsetX);
    const dayTimestamp = this.transformToNearestXAxisValue(new Date(timestamp));
    let scrollPoint = this.dates.indexOf(dayTimestamp);

    if(scrollPoint === -1 && dragDelta.offsetX >= right) {
      scrollPoint = this.dates.length - 1;
    }
    const difference = scrollPoint - dragStart;

    const range = this.dates.indexOf(staticScaleMax) - this.dates.indexOf(staticScaleMin);

    const minVal = this.dates.indexOf(staticScaleMax) + difference - range; // 0

    const maxVal = this.dates.indexOf(staticScaleMax) + difference; // 199

    let minChart1;
    let maxChart1;

    if(minVal <= 0 && dragDelta.offsetX < right) {
      minChart1 = this.dates[0];
      maxChart1 = this.dates[range];
    } else if(maxVal >= this.dates.length - 1 || difference < 0 && dragDelta.offsetX >= right) {
      minChart1 = this.dates[this.dates.length - 1 - range];
      maxChart1 = this.dates[this.dates.length - 1];
    } else {
      minChart1 = this.dates[this.dates.indexOf(staticScaleMin) + difference];
      maxChart1 = this.dates[this.dates.indexOf(staticScaleMax) + difference];
    }

    if(minChart1 === undefined) {
      minChart1 = this.dates[0];
    }

    if(maxChart1 === undefined) {
      maxChart1 = this.dates[this.dates.length - 1];
    }

    if(minChart1 === this.dates[0]) {
      stockPriceChart.config.options.scales.x.min = this.dates[0];
      stockPriceChart.config.options.scales.x.max = stockPriceChart.config.options.scales.x.max;
    } else if(maxChart1 === this.dates[this.dates.length - 1]) {
      stockPriceChart.config.options.scales.x.min = stockPriceChart.config.options.scales.x.min;
      stockPriceChart.config.options.scales.x.max = this.dates[this.dates.length - 1];
    } else if(stockPriceChart.config.options.scales.x.min >= this.dates[0] && stockPriceChart.config.options.scales.x.max <= this.dates[this.dates.length - 1]) {
      stockPriceChart.config.options.scales.x.min = minChart1;
      stockPriceChart.config.options.scales.x.max = maxChart1;
    }

    this.stockPriceChart.update('none');
    this.chartNavigator.update('none');
    this.zoomBoxItem(minChart1, maxChart1, chart);
  }



}
